import {fetchEventSource} from '@microsoft/fetch-event-source';
import Cookies from 'js-cookie';
import {writable, type Invalidator, type Subscriber, type Unsubscriber} from 'svelte/store';
import {tError} from '../toast-utils';

const emptyUser: UserInfo = {
    id: '',
    name: '',
    email: '',
    ref_code: '',
    balance: 0,
    ai_points: 0,
    ai_points_bought: 0,
    is_admin: false,
    tariff: {
        id: '',
        title: '',
        until: 0,
        price: 0,
        group_id: '',
        queue_onetime_limit: 0,
        is_paid: false,
        is_demo: false,
        is_ai_available: false,
        is_automata_available: false,
        additional_css_selectors_count: 0,
    },
    qchat: {
        authId: '',
        authKey: '',
    },
};

export type MeStore = {
    subscribe(this: void, run: Subscriber<MeStoreData>, invalidate?: Invalidator<MeStoreData>): Unsubscriber;
};

export type MeStoreData = UserInfo & {initialized: boolean; authorized: boolean};

class UnauthorizedError extends Error {}

/**
 * Информация о текущем пользователе
 */
export const me = (() => {
    const {subscribe} = writable<MeStoreData>({...emptyUser, authorized: false, initialized: false}, set => {
        // Определяем состояние авторизации по наличию куки авторизации
        let authorized = !!Cookies.get('auth_session') || !!Cookies.get('auth_session_ssnone');

        if (!authorized) {
            set({...emptyUser, authorized: false, initialized: true});
            return;
        }

        const ctrl = new AbortController();

        fetchEventSource(`${import.meta.env.VITE_RAPI_URL}/me`, {
            openWhenHidden: true,
            signal: ctrl.signal,
            onmessage(ev) {
                switch (ev.event) {
                    case 'error':
                        const error = JSON.parse(ev.data);

                        if (error.unauthorized) {
                            console.error('Unauthorized', error);
                            throw new UnauthorizedError('Unauthorized /me');
                        }

                        if (error.message) {
                            tError('Произошла ошибка: ' + error.message);
                        } else {
                            tError('Произошла ошибка');
                        }

                        break;

                    case 'user':
                        const user = JSON.parse(ev.data);
                        if (!user.authorized) {
                            set({...emptyUser, initialized: true, authorized: false});
                            break;
                        }

                        set({
                            initialized: true,
                            authorized: true,
                            id: user.id,
                            name: user.name,
                            email: user.email,
                            ref_code: user.ref_code,
                            balance: user.balance,
                            ai_points: user.ai_points,
                            ai_points_bought: user.ai_points_bought,
                            is_admin: user.group_id >= 1000,
                            tariff: {
                                ...user.tariff,
                                is_paid: user.tariff.until > Date.now(),
                            },
                            qchat: user.qchat,
                        });
                        break;

                    default:
                        console.log(ev);
                        break;
                }
            },
            onclose() {
                // if the server closes the connection unexpectedly, retry:
                throw new Error('/me connection closed. Retrying...');
            },
            onerror(err) {
                if (err instanceof UnauthorizedError) {
                    tError('Проблемы с авторизацией. Попробуйте перезагрузить страницу');
                    return;
                }

                // Retry - no exception
                console.error('Error in /me', err);
            },
        });

        return () => {
            ctrl.abort();
        };
    });

    return {
        subscribe,
    };
})();
